/*
 * Copyright (c) 2020. American Express Company
 */

import _ from 'lodash';
import { LocalDate } from '@js-joda/core';

import CategoryService from '../../services/category.service';
import IncomeSourceService from '../../services/income-source.service';
import RuleService from '../../services/rule.service';
import TransactionService, { Frequency } from '../../services/transaction.service';
import { AccountModel } from 'src/types/account.model';
import { CategoryModel } from 'src/types/category.model';
import { IncomeSourceModel } from 'src/types/income-source.model';
import { RuleModel } from 'src/types/rule.model';
import { TransactionModel } from 'src/types/transaction.model';

import { Summary } from 'src/types/user.model';
import { Board, SortOrder } from 'src/bw/board/Board';
import { IncomeSourceRuleModel } from 'src/types/income-source-rule.model';
import IncomeSourceRuleService from '../../services/income-source-rule.service';
import { TransactionField } from 'src/bw/project-jaeger/data/TransactionField.enum';
import NavigationService from 'src/services/navigation.service';
import { RuleMatcher } from 'src/types/ruleMatcher.enum';
import { matchesRule } from 'src/utils/rule.util';

export default class TransactionFactory {
    private loaderTimeout: number;
    private readonly component: Board;

    constructor(component: any) {
        this.component = component;
    }

    public showLoader(): void {
        this.loaderTimeout = window.setTimeout(() => {
            this.component.setState({
                waitMessages: ['Updating your Transactions', 'This may take a minute.']
            });
        }, 100);
    }

    public hideLoader(): void {
        window.clearTimeout(this.loaderTimeout);
        this.component.setState({
            waitMessages: []
        });
        this.loaderTimeout = undefined;
    }

    public async handleOpenTransactionWithId(
        startDate: LocalDate,
        endDate: LocalDate,
        transactionId: number
    ): Promise<void> {
        void NavigationService.goTo(
            this.component.props.history,
            `/drawer/transactions?start=${startDate}&end=${endDate}&selections=${transactionId}`,
            'Transactions Drawer'
        );
    }

    public async handleDisplayTransaction(transaction: TransactionModel): Promise<void> {
        const drawerProps: any = this.component.state.drawerProps;

        transaction.ignore = !transaction.ignore;
        await TransactionService.updateTransaction(transaction);

        this.component.drawerFactory.updateDrawer({
            ...this.component.state.drawerProps,
            transactions: drawerProps.transactions,
            transactionsChanged: true
        });

        if (transaction.type === 'TRANSACTION_INCOME') {
            this.component.drawerFactory.updateDrawer({
                ...this.component.state.drawerProps,
                incomeChanged: true
            });
        }
    }

    public async handleIgnoreFutureTransaction(transaction: TransactionModel): Promise<void> {
        const drawerProps: any = this.component.state.drawerProps;

        transaction.ignoreInFuture = !transaction.ignoreInFuture;
        await TransactionService.updateTransaction(transaction);

        this.component.drawerFactory.updateDrawer({
            ...this.component.state.drawerProps,
            transactions: drawerProps.transactions,
            transactionsChanged: true
        });

        if (transaction.type === 'TRANSACTION_INCOME') {
            this.component.drawerFactory.updateDrawer({
                ...this.component.state.drawerProps,
                incomeChanged: true
            });
        }
    }

    public async handleEditTransaction(transaction: TransactionModel): Promise<void> {
        this.component.drawerFactory.updateDrawer({
            ...this.component.state.drawerProps,
            selectedTransaction: transaction,
            selectedTransactionGroup: transaction.group
        });
    }

    public updateTransactionSummaries(transaction: TransactionModel, groupId: string): Summary[] {
        // update summary totals without the overhead of hitting the backend
        const summaries = this.component.props.user.summaries;

        const oldSummary: any = _.filter(summaries, {
            id: transaction.groupUid
        });
        if (oldSummary.length > 0) {
            oldSummary[0].sum -= transaction.amount;
        }

        const newSummary: any = _.filter(summaries, {
            id: groupId
        });
        if (newSummary.length > 0) {
            (newSummary[0].sum as number) += transaction.amount;
        }

        return summaries;
    }

    /**
     * Changing a transaction group.
     *
     * TODO: this takes groupID as a number if it's an income source, or a string if it's an expense category
     *
     * @param transaction
     * @param groupUid
     * @param remove
     */
    public async handleChangeTransactionGroup(
        transaction: TransactionModel,
        groupUid: string,
        remove: boolean = true
    ): Promise<void> {
        const drawerProps: any = this.component.state.drawerProps;
        let transactions: TransactionModel[] = drawerProps.transactions;

        const categories: CategoryModel[] = await CategoryService.getCategories();

        if (transaction.type === 'TRANSACTION_EXPENSE') {
            const category = categories ? _.find(categories, { id: groupUid }) : null;
            const summaries = this.component.transactionFactory.updateTransactionSummaries(transaction, groupUid);
            // force ui to redraw
            const updatedUser: any = { ...this.component.props.user };
            updatedUser.summaries = summaries;
            this.component.updateUserLocally(updatedUser);

            transaction.groupUid = groupUid;
            transaction.group = category ? category.name : '';
        } else {
            transaction.incomeUid = groupUid;
        }

        await TransactionService.updateTransaction(transaction);

        if (remove) {
            if (drawerProps.transactionsCategory) {
                // make this transaction disappear form the drawer
                transactions = transactions.filter((trans) => {
                    return transaction.id !== trans.id;
                });
            }
        } else {
            const index = _.findIndex(transactions, { id: transaction.id });
            transactions.splice(index, 1, transaction);
        }

        this.component.drawerFactory.updateDrawer({
            ...drawerProps,
            transactions,
            transactionsChanged: true,
            categories
        });
    }

    public async handleSortTransaction(name: string): Promise<void> {
        const drawerProps: any = this.component.state.drawerProps;
        let transactions: TransactionModel[] = drawerProps.transactions;
        let sort = drawerProps.transactionSort;

        if (sort && sort.sort === name) {
            sort.order = -sort.order;
        } else {
            sort = {
                sort: name,
                order: 1
            };
        }

        transactions = this.sortTransactions(transactions, sort.sort, sort.order);

        this.component.drawerFactory.updateDrawer({
            ...this.component.state.drawerProps,
            transactions,
            transactionSort: sort,
            selectedTransactionGroup: 0,
            selectedTransaction: null
        });
    }

    public async handleSortUnifiedTransaction(name: TransactionField): Promise<void> {
        const drawerProps: any = this.component.state.drawerProps;
        let transactions: TransactionModel[] = drawerProps.filteredTransactions;
        let sort = drawerProps.transactionSort;

        if (sort && sort.sort === name) {
            sort.order = -sort.order;
        } else {
            sort = {
                sort: name,
                order: 1
            };
        }

        transactions = this.sortTransactions(transactions, sort.sort, sort.order);

        let displayedTransactions = transactions;
        if (drawerProps.filteredTransactions.length > drawerProps.pageSize) {
            const start: number = (drawerProps.page - 1) * drawerProps.pageSize;
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            displayedTransactions = transactions.slice(start, start + drawerProps.pageSize - 1);
        }

        this.component.drawerFactory.updateDrawer({
            ...this.component.state.drawerProps,
            filteredTransactions: transactions,
            displayedTransactions,
            transactionSort: sort,
            selectedTransactionGroup: 0,
            selectedTransaction: null,
            page: 1
        });
    }

    public sortTransactions(transactions: TransactionModel[], sort: string, order: number): TransactionModel[] {
        let accountsById: Map<number, AccountModel> = this.component.props.userAccounts;

        if (!accountsById) {
            const accounts = this.component.props.user.accounts;
            accountsById = new Map<number, AccountModel>();
            for (const account of accounts) {
                accountsById.set(account.id, account);
            }
        }

        const incomes = this.component.state.drawerProps.incomeSources;

        switch (sort) {
            case 'name':
                transactions = transactions.sort((a: TransactionModel, b: TransactionModel) => {
                    return a.name.toLowerCase() > b.name.toLowerCase() ? order : -order;
                });
                break;
            case 'amount':
                transactions = transactions.sort((a: TransactionModel, b: TransactionModel) => {
                    return (a.amount - b.amount) * order;
                });
                break;
            case 'categories':
                transactions = transactions.sort((a: TransactionModel, b: TransactionModel) => {
                    return a.categories > b.categories ? order : -order;
                });
                break;
            case 'category':
                transactions = transactions.sort((a: TransactionModel, b: TransactionModel) => {
                    let aCategory = a.group.toLowerCase();
                    if (a.incomeUid && incomes) {
                        const income = _.find(incomes, { incomeUid: a.incomeUid });
                        if (income) aCategory = income.name.toLowerCase();
                    }

                    let bCategory = b.group.toLowerCase();
                    if (b.incomeUid && incomes) {
                        const income = _.find(incomes, { incomeUid: b.incomeUid });
                        if (income) bCategory = income.name.toLowerCase();
                    }

                    return aCategory > bCategory ? order : -order;
                });
                break;
            case 'date':
                transactions = transactions.sort((a: TransactionModel, b: TransactionModel) => {
                    if (_.isEqual(a.date, b.date)) {
                        return a.name.toLowerCase() > b.name.toLowerCase() ? order : -order;
                    }
                    return a.date < b.date ? order : -order;
                });
                break;
            case 'account':
                transactions = transactions.sort((a: TransactionModel, b: TransactionModel) => {
                    const accountA: AccountModel = accountsById.get(a.accountId);
                    const accountB: AccountModel = accountsById.get(b.accountId);
                    return accountA.name > accountB.name ? order : -order;
                });
                break;
            case 'display':
                transactions = transactions.sort((a: TransactionModel, b: TransactionModel) => {
                    return a.ignore > b.ignore ? order : -order;
                });
                break;
            case 'occurrence':
                transactions = transactions.sort((a: TransactionModel, b: TransactionModel) => {
                    return this.occurrenceSort(a) > this.occurrenceSort(b) ? order : -order;
                });
                break;
            case 'type':
                transactions = transactions.sort((a: TransactionModel, b: TransactionModel) => {
                    return a.type > b.type ? order : -order;
                });
                break;
        }

        return transactions;
    }

    public occurrenceSort(transaction: TransactionModel): number {
        if (transaction.ignore) {
            return 0;
        } else if (transaction.ignoreInFuture) {
            return 1;
        } else {
            return 2;
        }
    }

    public async handleRecategorizeExpenseTransaction(
        transaction: TransactionModel,
        groupId: string,
        ruleType: RuleMatcher,
        ruleText: string,
        remove: boolean = false
    ): Promise<void> {
        try {
            this.showLoader();
            const drawerProps: any = this.component.state.drawerProps;
            const category: CategoryModel = _.find(drawerProps.categories, { id: groupId });
            const categoryName: string = category.name;

            // make a rule
            const newRule = new RuleModel();
            newRule.priority = 0;
            newRule.transactionName = ruleText;
            newRule.groupUid = category.id;
            newRule.matcher = ruleType;
            await RuleService.createRule(newRule);

            let transactions: TransactionModel[] = drawerProps.transactions;
            const matched: TransactionModel[] = [];

            for (const transaction of drawerProps.transactions) {
                if (matchesRule(transaction, newRule)) {
                    matched.push(transaction);
                }
            }

            for (const transaction of matched) {
                (transaction as any).categoryName = categoryName;
                transaction.groupUid = groupId;

                await TransactionService.updateTransaction(transaction);
                if (remove) {
                    transactions = _.pull(transactions, transaction);
                } else {
                    const index = _.findIndex(transactions, { id: transaction.id });
                    if (index !== -1) {
                        transactions[index] = transaction;
                    }
                }
            }

            this.component.drawerFactory.updateDrawer({
                ...drawerProps,
                transactions,
                transactionsChanged: true
            });
        } finally {
            this.hideLoader();
        }
    }

    public async handleChangeIncomeSource(
        transaction: TransactionModel,
        incomeUid: string,
        remove: boolean = false
    ): Promise<void> {
        (transaction as any).incomeUid = incomeUid;
        transaction = await TransactionService.updateTransaction(transaction);

        const transactions: TransactionModel[] = this.component.state.drawerProps.transactions;

        if (remove) {
            _.remove(transactions, (t: TransactionModel) => {
                return t.id === transaction.id;
            });
        } else {
            const index = _.findIndex(transactions, { id: transaction.id });
            if (index !== -1) {
                transactions[index] = transaction;
            }
        }

        this.component.updateTransactions(transaction);

        this.component.drawerFactory.updateDrawer({
            ...this.component.state.drawerProps,
            transactions
        });
    }

    public async openTransactionsRoute(
        startDate: LocalDate,
        endDate: LocalDate,
        params: any = {},
        state = 'unified-transactions'
    ): Promise<any> {
        let transactions: TransactionModel[];

        if (!params.filters) params.filters = {};

        if (params.filters.review) {
            transactions = await TransactionService.getTransactionsForReview();
            state = 'review-transactions';
        } else {
            transactions = await TransactionService.getTransactions(
                startDate || this.component.props.user.cashFlow[0].startDate,
                endDate || LocalDate.now().plusDays(1)
            );
            if (params.filters.selections) {
                transactions = _.filter(transactions, (transaction) => {
                    return String(transaction.id) === params.filters.selections;
                });
            }
        }

        const categories: CategoryModel[] = await CategoryService.getCategories();
        const incomeSources: IncomeSourceModel[] = _.sortBy(
            await IncomeSourceService.getIncomeSources(),
            (i) => i.name
        );
        const accounts = this.component.props.user.accounts;
        const includedAccounts = _.chain(transactions).map('accountId').uniq().value();
        const sort: SortOrder = { sort: 'date', order: 1 };
        const pageSize: number = state === 'account' ? 8 : 50;

        let drawerProps: any = {
            account: params.filters.account ? Number(params.filters.account) : null,
            accounts,
            currentAccount: params.filters.currentAccount,
            filters: params.filters,
            hiddenAccounts: params.filters.hiddenAccounts,
            includedAccounts,
            incomeSource: params.filters.incomeSource,
            incomeSources,
            transactions,
            transactionSort: sort,
            transactionsChanged: false,
            startDate,
            endDate,
            categories,
            groupUid: params.filters.group,
            filteredTransactions: transactions,
            selectedTransactions: [],
            pageSize,
            page: params.filters.page ? Number(params.filters.page) : 1,
            search: params.filters.search,
            state: params.filters.state,
            type: params.filters.type,
            review: params.filters.review,
            mentor: params.filters.mentor,
            user: this.component.props.user
        };

        drawerProps = this.updateFilteredTransactions(drawerProps);

        this.component.setState({
            drawerState: state,
            drawerProps
        });
    }

    public updateFilteredTransactions(drawerProps: any): any {
        drawerProps.filteredTransactions = drawerProps.transactions;

        if (!drawerProps.showHidden) {
            drawerProps.filteredTransactions = _.filter(
                drawerProps.filteredTransactions,
                (transaction) => !transaction.ignore
            );
        }

        if (drawerProps.search && drawerProps.search.length > 0) {
            // if the search is a number, it could be looking for an exact id
            const numSearch = _.toNumber(drawerProps.search);
            if (!isNaN(numSearch)) {
                drawerProps.filteredTransactions = _.filter(
                    drawerProps.filteredTransactions,
                    (transaction) =>
                        transaction.id === numSearch ||
                        // or it could be a number in the name, so search for the number as a substring
                        transaction.displayName.indexOf(drawerProps.search) !== -1
                );
            } else {
                // not a number search, compare ignoring case
                drawerProps.filteredTransactions = _.filter(
                    drawerProps.filteredTransactions,
                    (transaction) =>
                        transaction.displayName.toUpperCase().indexOf(drawerProps.search.toUpperCase()) !== -1
                );
            }
        }

        if (drawerProps.account && Number(drawerProps.account) !== -1) {
            drawerProps.filteredTransactions = _.filter(drawerProps.filteredTransactions, (transaction) => {
                return transaction.accountId === Number(drawerProps.account);
            });
        }

        if (drawerProps.type && drawerProps.type.length > 0) {
            drawerProps.filteredTransactions = _.filter(drawerProps.filteredTransactions, (transaction) => {
                return transaction.type === drawerProps.type;
            });
        }

        if (drawerProps.groupUid) {
            const groupUids = _.filter(drawerProps.categories, (category) => {
                return category.id === drawerProps.groupUid || category.parentId === drawerProps.groupUid;
            }).map((category) => category.id);
            drawerProps.filteredTransactions = _.filter(drawerProps.filteredTransactions, (transaction) => {
                return transaction.type === 'TRANSACTION_EXPENSE' && groupUids.includes(transaction.groupUid);
            });
        }

        if (drawerProps.incomeSource && drawerProps.incomeSource.length > 0) {
            drawerProps.filteredTransactions = _.filter(drawerProps.filteredTransactions, (transaction) => {
                return transaction.type === 'TRANSACTION_INCOME' && transaction.incomeUid === drawerProps.incomeSource;
            });
        }

        drawerProps.filteredTransactions = this.sortTransactions(
            drawerProps.filteredTransactions,
            drawerProps.transactionSort.sort,
            drawerProps.transactionSort.order
        );

        drawerProps.displayedTransactions = drawerProps.filteredTransactions;
        if (drawerProps.filteredTransactions.length > drawerProps.pageSize) {
            const start = (drawerProps.page - 1) * drawerProps.pageSize;
            drawerProps.displayedTransactions = drawerProps.filteredTransactions.slice(
                start,
                start + (drawerProps.pageSize as number)
            );
        }

        drawerProps.selectedTransactions = _.filter(drawerProps.selectedTransactions, (transaction) =>
            drawerProps.displayedTransactions.includes(transaction)
        );

        return drawerProps;
    }

    public async setTransactions(drawerProps: any, startDate?: LocalDate, endDate?: LocalDate): Promise<any> {
        const transactions: TransactionModel[] = await TransactionService.getTransactions(
            startDate || this.component.props.user.cashFlow[0].startDate,
            endDate || LocalDate.now().plusDays(1)
        );
        const includedAccounts: number[] = _.chain(transactions).map('accountId').uniq().value();
        drawerProps.transactions = transactions;
        drawerProps.includedAccounts = includedAccounts;
        drawerProps.startDate = startDate;
        drawerProps.endDate = endDate;

        const newDrawerProps: any = this.updateFilteredTransactions(drawerProps);
        return newDrawerProps;
    }

    public async recategorizeSelectedExpenses(changeToGroupUid: string, moveAll: boolean): Promise<void> {
        this.showLoader();
        const drawerProps: any = this.component.state.drawerProps;
        const category: CategoryModel = _.find(drawerProps.categories, { id: changeToGroupUid });
        if (!category) return;
        const categoryName: string = category.name;
        const matched: TransactionModel[] = [];
        const newTransactions: TransactionModel[] = drawerProps.transactions;
        const newSelected: TransactionModel[] = drawerProps.selectedTransactions;

        for (const transaction of drawerProps.selectedTransactions) {
            if (moveAll) {
                // make a rule
                const newRule = new RuleModel();
                newRule.priority = 0;
                newRule.transactionName = transaction.displayName;
                newRule.groupUid = changeToGroupUid;
                newRule.matcher = RuleMatcher.CONTAINS;
                await RuleService.createRule(newRule);

                for (const rTransaction of drawerProps.transactions) {
                    if (matchesRule(rTransaction, newRule)) {
                        matched.push(rTransaction);
                        rTransaction.groupUid = changeToGroupUid;
                        rTransaction.group = category.name;
                        rTransaction.categoryName = categoryName;
                        this.updateTransactions(newTransactions, rTransaction);
                        this.updateTransactions(newSelected, rTransaction);
                    }
                }
            }

            transaction.groupUid = changeToGroupUid;
            transaction.group = category.name;
            transaction.categoryName = categoryName;
            this.updateTransactions(newTransactions, transaction);
            this.updateTransactions(newSelected, transaction);
        }

        await TransactionService.updateTransactionsGroup(
            _.union(drawerProps.selectedTransactions, matched),
            changeToGroupUid
        );

        this.component.drawerFactory.updateDrawer({
            ...drawerProps,
            transactions: newTransactions,
            selectedTransactions: [],
            transactionsChanged: true
        });

        this.hideLoader();
    }

    public async recategorizeSelectedIncomes(changeToIncomeUid: string, moveAll: boolean): Promise<void> {
        this.showLoader();
        const drawerProps: any = this.component.state.drawerProps;
        const matched: TransactionModel[] = [];
        const newTransactions: TransactionModel[] = drawerProps.transactions;
        const newSelected: TransactionModel[] = drawerProps.selectedTransactions;

        for (const transaction of drawerProps.selectedTransactions) {
            if (moveAll) {
                // make a rule
                const newRule = new IncomeSourceRuleModel();
                newRule.transactionNamePattern = transaction.displayName;
                newRule.incomeUid = changeToIncomeUid;
                newRule.matcher = RuleMatcher.CONTAINS;
                await IncomeSourceRuleService.createRule(newRule);

                for (const rTransaction of drawerProps.transactions) {
                    if (IncomeSourceRuleService.matchesRule(rTransaction, newRule)) {
                        matched.push(rTransaction);
                        rTransaction.incomeUid = changeToIncomeUid;
                        this.updateTransactions(newTransactions, rTransaction);
                        this.updateTransactions(newSelected, rTransaction);
                    }
                }
            }

            transaction.incomeUid = changeToIncomeUid;
            this.updateTransactions(newTransactions, transaction);
            this.updateTransactions(newSelected, transaction);
        }

        await TransactionService.updateTransactionsIncomeSource(
            _.union(drawerProps.selectedTransactions, matched),
            changeToIncomeUid
        );

        this.component.drawerFactory.updateDrawer({
            ...drawerProps,
            transactions: newTransactions,
            selectedTransactions: [],
            transactionsChanged: true
        });

        this.hideLoader();
    }

    public async changeFrequenceOfSelected(changeToFrequency: string): Promise<void> {
        this.showLoader();
        const drawerProps = this.component.state.drawerProps;
        const newTransactions = drawerProps.transactions;
        const newSelected: TransactionModel[] = drawerProps.selectedTransactions;
        let ignore: boolean = true;
        let ignoreInFuture: boolean = true;

        switch (changeToFrequency) {
            case Frequency.TYPICAL:
                ignoreInFuture = false;
                ignore = false;
                break;

            case Frequency.ONETIME:
                ignore = false;
                break;

            case Frequency.IGNORE:
                ignoreInFuture = false;
                break;
        }

        for (const transaction of drawerProps.selectedTransactions) {
            transaction.ignoreInFuture = ignoreInFuture;
            transaction.ignore = ignore;
            this.updateTransactions(newSelected, transaction);
            this.updateTransactions(newTransactions, transaction);
        }

        await TransactionService.updateTransactionsIgnore(drawerProps.selectedTransactions, ignore, ignoreInFuture);

        this.component.drawerFactory.updateDrawer({
            ...drawerProps,
            transactions: newTransactions,
            selectedTransactions: [],
            transactionsChanged: true
        });

        this.hideLoader();
    }

    public updateTransactions(transactions: TransactionModel[], transaction: TransactionModel): void {
        const index = _.findIndex(transactions, { id: transaction.id });
        if (index !== -1) {
            transactions[index] = transaction;
        }
    }
}
